import { useMemo, useRef, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { SelectSearchBase } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { useDebouncedValue } from "@shared/hooks";

import {
  useInspectionParticipantUsersQuery,
  type InspectionParticipantUser,
} from "../../api/inspection";

export interface InspectionParticipantUserComboboxProps {
  structureId: string;
  value: string;
  onChange: (userId: string, user: InspectionParticipantUser | null) => void;
  disabled?: boolean;
  required?: boolean;
  excludedUserIds?: string[];
}

const SEARCH_DEBOUNCE_MS = 300;
const PARTICIPANT_USERS_PAGE_SIZE = 100;

/**
 * Seletor de usuário do tenant para participante extra (Y=15 / QT-INSP-05-05).
 * Não reusa o UserCombobox do Admin — aquele GET é 403 para operador só com
 * `create` em Inspeção. A busca é no servidor (`q`); a primeira página sem
 * termo não é o universo do tenant (QT-INSP-05-15).
 */
const InspectionParticipantUserCombobox = ({
  structureId,
  value,
  onChange,
  disabled = false,
  required = false,
  excludedUserIds = [],
}: InspectionParticipantUserComboboxProps) => {
  const { translate } = useLocalization();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const usersById = useRef(new Map<string, InspectionParticipantUser>());

  const { data, isLoading } = useInspectionParticipantUsersQuery(
    structureId,
    {
      page: 1,
      limit: PARTICIPANT_USERS_PAGE_SIZE,
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
    },
    { placeholderData: keepPreviousData },
  );

  for (const user of data?.data ?? []) {
    usersById.current.set(user.id, user);
  }

  const excluded = useMemo(() => new Set(excludedUserIds), [excludedUserIds]);

  const options = useMemo(() => {
    const listed = (data?.data ?? []).filter((user) => !excluded.has(user.id));
    if (value && !listed.some((user) => user.id === value)) {
      const selected = usersById.current.get(value);
      if (selected && !excluded.has(selected.id)) {
        listed.unshift(selected);
      }
    }

    return listed.map((user) => ({
      label: `${user.name} — ${user.email}`,
      value: user.id,
    }));
  }, [data?.data, excluded, value]);

  const isDisabled = disabled || isLoading;

  return (
    <div
      className="insp-field"
      onInput={(event) => {
        if (event.target instanceof HTMLInputElement) {
          setSearch(event.target.value);
        }
      }}
    >
      <label className={isDisabled ? "text-text-disabled" : undefined}>
        {translate("inspection.form.gs3User")}
        {required ? (
          <>
            {" "}
            <span className="insp-req" aria-hidden>
              *
            </span>
          </>
        ) : null}
      </label>
      <SelectSearchBase
        value={value}
        onChange={(next) => {
          const userId = typeof next === "string" ? next : "";
          const user =
            (data?.data ?? []).find((item) => item.id === userId) ??
            usersById.current.get(userId) ??
            null;
          onChange(userId, user);
        }}
        placeholder={translate("inspection.form.gs3UserPlaceholder")}
        options={options}
        disabled={isDisabled}
        clearable
        notFoundText={translate("inspection.form.gs3UserNotFound")}
      />
    </div>
  );
};

export default InspectionParticipantUserCombobox;
